import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { ActivityParentEntityId } from "@sp-crm/core";
import { useAgendaRefresh } from "components/marketing-plan/use-agenda-refresh";
import { MessageCompose } from "components/messages/message-compose";
import { Setting } from "components/setting";
import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";
import { mailtoUri } from "util/uri";
import {
    ContactValueLabel,
    ContactValueRow,
    contactValueLinkClasses,
} from "./display-row";

interface EmailComposeTriggerProps {
    value: string;
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
    renderTrigger: (
        open: (e: React.MouseEvent<HTMLAnchorElement>) => void,
    ) => React.ReactNode;
}

export const EmailComposeTrigger: React.FC<EmailComposeTriggerProps> = props => {
    const { value, entityId, entityType, renderTrigger } = props;

    const [composing, setComposing] = React.useState(false);
    const refreshAgenda = useAgendaRefresh();

    const openCompose = React.useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setComposing(true);
    }, []);

    const dismissCompose = React.useCallback(() => {
        setComposing(false);
        refreshAgenda();
    }, [refreshAgenda]);

    return (
        <>
            {renderTrigger(openCompose)}
            {composing ? (
                <MessageCompose
                    entityId={entityId}
                    entityType={entityType}
                    initialRecipients={[value]}
                    onDismiss={dismissCompose}
                />
            ) : null}
        </>
    );
};

interface EmailOptOutChipProps {
    optOut: boolean;
}

export const EmailOptOutChip: React.FC<EmailOptOutChipProps> = props => {
    const { optOut } = props;

    if (!optOut) {
        return null;
    }

    return (
        <Setting name="emailOptOut">
            <span
                className="flex-shrink-0 text-xs text-gray-500"
                title="Opted out of bulk email">
                no bulk email
            </span>
        </Setting>
    );
};

interface EmailLinkProps {
    value: string;
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
}

export const EmailLink: React.FC<EmailLinkProps> = props => {
    const { value, entityId, entityType } = props;

    if (!value) {
        return null;
    }

    return (
        <EmailComposeTrigger
            value={value}
            entityId={entityId}
            entityType={entityType}
            renderTrigger={open => (
                <a
                    href={mailtoUri(value)}
                    title={`Email ${value}`}
                    onClick={open}
                    className={contactValueLinkClasses}>
                    {value}
                </a>
            )}
        />
    );
};

interface EmailDisplayProps {
    value: string;
    label: string | null;
    optOut: boolean;
    entityId: ActivityParentEntityId;
    entityType: AdvancedSearchEntityType;
}

export const EmailDisplay: React.FC<EmailDisplayProps> = props => {
    const { value, label, optOut, entityId, entityType } = props;

    if (!value) {
        return null;
    }

    return (
        <ContactValueRow Icon={EnvelopeIcon}>
            <EmailLink value={value} entityId={entityId} entityType={entityType} />
            <ContactValueLabel label={label} />
            <EmailOptOutChip optOut={optOut} />
        </ContactValueRow>
    );
};
